import { useCallback, useEffect, useRef, useState } from "react";
import {
  VO_PRESENCE_ALERT_MS,
  collectNewPresenceAlerts,
  notePresenceEvent,
  voPresenceAlertCopy,
} from "./virtualOfficePresenceAlerts";
import { playVoPresenceSound, unlockVoPresenceAudio } from "./virtualOfficePresenceSounds";

export function useVirtualOfficePresenceAlerts({
  people = [],
  activity = [],
  selfUserId,
  socket,
  enabled = true,
}) {
  const [alerts, setAlerts] = useState([]);
  const [highlightUserId, setHighlightUserId] = useState(0);
  const seenRef = useRef(new Set());
  const prevPeopleRef = useRef(null);
  const hydratedRef = useRef(false);

  const pushAlerts = useCallback((incoming, { play = true } = {}) => {
    if (!incoming?.length) return;
    setAlerts((prev) => {
      const known = new Set(prev.map((a) => a.key));
      const next = incoming.filter((a) => a.key && !known.has(a.key));
      if (!next.length) return prev;
      return [...next, ...prev].slice(0, 6);
    });
    const firstEnter = incoming.find((a) => a.type === "enter");
    if (firstEnter?.userId) {
      setHighlightUserId(Number(firstEnter.userId));
      window.setTimeout(() => {
        setHighlightUserId((cur) => (cur === Number(firstEnter.userId) ? 0 : cur));
      }, VO_PRESENCE_ALERT_MS);
    }
    if (play) {
      incoming.forEach((a, idx) => {
        window.setTimeout(() => {
          void playVoPresenceSound(a.sound);
        }, idx * 420);
      });
    }
    incoming.forEach((a) => {
      window.setTimeout(() => {
        setAlerts((prev) => prev.filter((x) => x.key !== a.key));
      }, VO_PRESENCE_ALERT_MS);
    });
  }, []);

  useEffect(() => {
    unlockVoPresenceAudio();
    const unlock = () => unlockVoPresenceAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const prev = hydratedRef.current ? prevPeopleRef.current : null;
    const { alerts: next, seenKeys } = collectNewPresenceAlerts({
      prevPeople: prev,
      nextPeople: people,
      activity,
      selfUserId,
      seenKeys: seenRef.current,
    });
    seenRef.current = seenKeys;
    prevPeopleRef.current = people;
    hydratedRef.current = true;
    pushAlerts(next);
  }, [enabled, people, activity, selfUserId, pushAlerts]);

  useEffect(() => {
    if (!socket) return undefined;
    const onVideoRoom = (raw) => {
      let payload = raw;
      if (typeof raw === "string") {
        try {
          payload = JSON.parse(raw);
        } catch {
          payload = null;
        }
      }
      const action = String(payload?.action || "");
      const ev = payload?.presenceEvent;
      if (action !== "vo_presence" || !ev?.userId) return;
      if (Number(ev.userId) === Number(selfUserId)) return;
      const normalized = {
        type: ev.type === "leave" ? "leave" : "enter",
        id: ev.id || null,
        leaveKind: ev.leaveKind,
        userId: Number(ev.userId),
        userName: ev.userName,
        userPhoto: ev.userPhoto,
        roomId: Number(ev.roomId) || 0,
        at: ev.at || null,
      };
      const { isNew, key } = notePresenceEvent(seenRef.current, normalized);
      if (!isNew) return;
      pushAlerts([{ ...normalized, key, ...voPresenceAlertCopy(normalized) }]);
    };
    socket.on("video_room", onVideoRoom);
    return () => socket.off("video_room", onVideoRoom);
  }, [socket, selfUserId, pushAlerts]);

  const dismissAlert = useCallback((key) => {
    setAlerts((prev) => prev.filter((a) => a.key !== key));
  }, []);

  return { alerts, highlightUserId, dismissAlert };
}
